import { RESIDENT_LIST_DEFAULT_QUERY } from '../constants/residentConstants'
import type { ResidentListFilters, ResidentListQuery } from '../types'

/** UI-only fields stored in session — never sent to `/api/resident`. */
const RESIDENT_LIST_METADATA_KEYS = new Set(['company_name', 'property_name'])

function trimValue(value: unknown): unknown {
  return typeof value === 'string' ? value.trim() : value
}

type RoleScope = {
  companyId?: string | number | null
  companyName?: string | null
  propertyId?: string | number | null
  propertyName?: string | null
}

/**
 * Angular `resident-list` init: default pagination/sort + session filter + role locks.
 */
export function buildInitialResidentListQuery(
  role: RoleScope,
  session: Partial<ResidentListQuery> = {},
): ResidentListQuery {
  return {
    ...RESIDENT_LIST_DEFAULT_QUERY,
    ...session,
    ...(role.companyId
      ? {
          companyId: String(role.companyId),
          company_name: role.companyName || undefined,
        }
      : {}),
    ...(role.propertyId
      ? {
          propertyId: String(role.propertyId),
          property_name: role.propertyName || undefined,
        }
      : {}),
  }
}

export function buildResidentListQuery(
  current: ResidentListQuery,
  filters: Partial<ResidentListFilters>,
): ResidentListQuery {
  const next: ResidentListQuery = {
    ...current,
    ...filters,
    page: filters && Object.keys(filters).some((key) => key !== 'page') ? 1 : current.page,
  }

  if ('companyId' in filters && !filters.companyId && !filters.company_name) {
    delete next.company_name
  }
  if ('propertyId' in filters && !filters.propertyId && !filters.property_name) {
    delete next.property_name
  }

  Object.entries(next).forEach(([key, value]) => {
    const trimmed = trimValue(value)
    if (
      trimmed === '' ||
      trimmed === undefined ||
      trimmed === null ||
      (Array.isArray(trimmed) && trimmed.length === 0)
    ) {
      delete (next as unknown as Record<string, unknown>)[key]
    } else {
      ;(next as unknown as Record<string, unknown>)[key] = trimmed
    }
  })

  return { ...RESIDENT_LIST_DEFAULT_QUERY, ...next }
}

export function buildResidentListRequestParams(query: Partial<ResidentListQuery>): Record<string, unknown> {
  const params: Record<string, unknown> = {}

  Object.entries(query).forEach(([key, value]) => {
    if (RESIDENT_LIST_METADATA_KEYS.has(key)) return
    if (value === undefined || value === null || value === '') return
    if (key === 'rentalStatus' && Array.isArray(value)) {
      params['rentalStatus[]'] = value
      return
    }
    if (key === 'defaultStatus' && Array.isArray(value)) {
      params['defaultStatus[]'] = value
      return
    }
    if (key === 'lease_start_daterange') return
    params[key] = value
  })

  return params
}

export function serializeResidentExportParams(query: Partial<ResidentListQuery>, token?: string | null): string {
  const params = buildResidentListRequestParams(query)
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const base = key.replace(/\[\]$/, '')
      value.forEach((item, index) => searchParams.append(`${base}[${index}]`, String(item)))
    } else if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  if (token) searchParams.append('access_token', token)
  searchParams.append('view', 'xlsx')
  return searchParams.toString()
}
